import {
  NJHeading,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/DataTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AdminGroup } from "@/types";
import { useAdminGroups } from "./useAdminGroups";

const PAGE_SIZE = 20;

/** Admin groups table — server-side search + pagination, whole-row click opens the detail. */
export function AdminGroupsPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query]);

  const { data, isLoading, isError } = useAdminGroups({
    q: query,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  const columnDefs = useMemo<ColDef<AdminGroup>[]>(
    () => [
      { headerName: "Name", field: "name", flex: 1 },
      { headerName: "Type", field: "type", flex: 1 },
      { headerName: "Description", field: "description", flex: 2 },
      { headerName: "Linked agent", field: "linkedAgentSlug", flex: 1 },
      { headerName: "Members", field: "membersCount", flex: 1 },
    ],
    [],
  );

  const onRowClicked = (event: RowClickedEvent<AdminGroup>) => {
    if (event.data) {
      navigate(`/admin/groups/${encodeURIComponent(event.data.name)}`);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <NJHeading as="h1" scale="lg">
        Administration — groups
      </NJHeading>

      <NJInputSearch
        label="Search groups"
        placeholder="name, type, agent"
        value={searchInput}
        onChange={(event) => setSearchInput(event.currentTarget.value)}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <NJSpinner aria-label="Loading groups" />
        </div>
      ) : isError ? (
        <NJInlineMessage variant="error">
          Could not load the groups.
        </NJInlineMessage>
      ) : (
        <>
          <div className="[&_.ag-row]:cursor-pointer">
            <DataTable<AdminGroup>
              rowData={data?.items ?? []}
              columnDefs={columnDefs}
              onRowClicked={onRowClicked}
            />
          </div>
          {data && data.items.length === 0 && (
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              No group matches this search.
            </NJText>
          )}
          {totalPages > 1 && (
            <div className="flex justify-end">
              <NJPagination
                count={totalPages}
                currentItem={page}
                ariaLabelPrevious="Previous groups page"
                ariaLabelNext="Next groups page"
                onChange={(item: number) => setPage(item)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
